import PropTypes from "prop-types";
import { IconCheckSizeMd } from "../../utils/Icons/IconCheckSizeMd";
import { IconXSizeMd } from "../../utils/Icons/IconXSizeMd";
import { IconWarning } from "../../utils/Icons/IconWarning";
import { IconInfo } from "../../utils/Icons/IconInfo";
import { IconCheck } from "../../utils/Icons/IconCheck";
import { IconX } from "../../utils/Icons/IconX";
import { IconWarningBlack } from "../../utils/Icons/IconWarningBlack";
import { IconLoading } from "../../utils/Icons/IconLoading";
import { AlertTriangle, CheckCircle, CheckCircle2, Download, Info, Loader2, LucideIcon, XCircle } from "lucide-react";
import "./style.css";
import { Button, Flex } from "@chakra-ui/react";

/**
 * GEN-3563 — o "X" que fecha o alerta.
 *
 * Extraído porque o mesmo bloco se repetia nos quatro ramos de `type` e a repetição já tinha
 * cobrado o preço: a primeira versão desta mudança pôs `role`/`aria-label`/`tabIndex` nos quatro,
 * mas em nenhum deles o handler de teclado — e `<div role="button">` NÃO dispara `click` com
 * Enter/Espaço como um `<button>` nativo faz. O resultado era pior que o ponto de partida: o
 * elemento entrava na ordem de Tab e era anunciado como "Fechar aviso, botão", mas não fazia nada
 * ao ser acionado. Com um componente só, o comportamento é impossível de divergir entre os ramos.
 *
 * Segue `div` em vez de `<button>` para não herdar o reset de estilo do `button` e não mexer no
 * CSS de `.action`, que já está estabelecido nos quatro ramos.
 */
const BotaoFecharAlerta = ({ onClose }: { onClose?: () => void }) => (
  <div
    className="action"
    onClick={onClose}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        // Espaço rola a página por padrão quando o alvo não é um controle nativo.
        e.preventDefault();
        onClose?.();
      }
    }}
    role="button"
    aria-label="Fechar aviso"
    tabIndex={0}
  >
    <IconXSizeMd className="instance-node" color="#171923" />
  </div>
);

type AlertStatus = "warning" | "success" | "error" | "info" | "loading" | "information";

/**
 * GEN-3646 — tom de cada status no aviso flutuante (`type="three"`), espelhando o
 * `ALERT_STATUS_TOKENS` do Kanban 2.0: ícone lucide na cor forte, título na cor escura do status.
 *
 * `info` deixou de desenhar o spinner: o ícone girando é exclusivo de `loading`. Antes, avisos só
 * informativos ("Você está agora no cliente X") pareciam algo em andamento (Nielsen #1).
 */
export const TOAST_TONE: Record<AlertStatus, { Icon: LucideIcon; iconColor: string; accent: string }> = {
  success: { Icon: CheckCircle2, iconColor: "#16A34A", accent: "#14532D" },
  error: { Icon: XCircle, iconColor: "#DC2626", accent: "#7F1D1D" },
  warning: { Icon: AlertTriangle, iconColor: "#D97706", accent: "#713F12" },
  info: { Icon: Info, iconColor: "#4B4EFF", accent: "#4236F5" },
  information: { Icon: Info, iconColor: "#4B4EFF", accent: "#4236F5" },
  loading: { Icon: Loader2, iconColor: "#4B4EFF", accent: "#4236F5" },
};

interface Props {
  /**
   * GEN-3646 — título e corpo como TEXTO (sem HTML), só para `type="three"`. Quando um dos dois vem,
   * substitui `descriptionHtml`. É o caminho do `notify()`; o `<div><b>…</b></div>` no HTML segue
   * funcionando para as chamadas antigas.
   */
  heading?: string;
  body?: string;
  isClosable?: boolean;
  closableFunc?: () => void;
  /** Conteúdo do alerta. Opcional quando `rows` é usado (o ramo multi-assunto o ignora). */
  descriptionHtml?: string;
  clicable?: {
    newLine?: boolean;
    action: () => void;
    text: string;
    /** "link" (default) para banners informativos; "button" para banners cuja razão de existir é uma ação primária. */
    variant?: "link" | "button";
  };
  /**
   * GEN-777 — segunda ação do banner, renderizada ANTES da principal e sempre como link.
   * Só vale para `type="banner"` sem `rows`. Opcional: sem ela nada muda nos banners atuais.
   */
  secondaryClicable?: {
    action: () => void;
    text: string;
    /** Enquanto verdadeiro o CTA fica opaco e não responde ao clique (ação em curso). */
    disabled?: boolean;
  };
  /**
   * GEN-2407 R8 — banner com MAIS DE UM assunto: cada item é uma linha com seu próprio CTA.
   * Só vale para `type="banner"` e substitui `descriptionHtml`/`clicable` quando presente.
   *
   * Nasceu de dois banners âmbar empilhados de mesmo peso na tela Consultas: nenhum lia como
   * mais importante e a faixa de alerta consumia meia tela antes da tabela (Nielsen #8 —
   * "estética e design minimalista"; #1 — status do sistema num lugar só). Somar os números num
   * único total seria pior: os conjuntos podem se sobrepor (uma consulta com pendência também
   * pode nunca ter sido pesquisada), então cada assunto mantém sua própria contagem e ação.
   */
  rows?: { html: string; action?: { text: string; onClick: () => void } }[];
  icon?: boolean;
  title?: boolean;
  status: AlertStatus;
  variant: "top-accent" | "solid" | "subtle" | "alert" | "left-accent";
  type: "two" | "three" | "one" | "four" | "banner";
  className: any;
  justify?: "start" | "space-between";
  style?: React.CSSProperties;
}

export const CustomAlert = ({
  heading,
  body,
  isClosable = true,
  closableFunc,
  descriptionHtml = "",
  clicable,
  secondaryClicable,
  rows,
  icon = true,
  title = true,
  status,
  variant,
  type,
  className,
  justify,
  style,
}: Props): JSX.Element => {
  // Cor do texto/CTA do banner padronizado (type="banner"), por status semântico.
  const bannerTextColor =
    status === "success" ? "#14532D" : status === "warning" ? "#713F12" : status === "error" ? "#7F1D1D" : "#4236f5"; // info / loading / information (primary-600 → contraste AA)

  // Ícone do banner padronizado, preservando a iconografia atual de cada status.
  const bannerIcon =
    status === "success" ? (
      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="instance-node"
      >
        <g id="SVGRepo_iconCarrier">
          {" "}
          <path
            opacity="0.3"
            d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
            fill="#4AD17C"
          />{" "}
          <path
            d="M16.0303 8.96967C16.3232 9.26256 16.3232 9.73744 16.0303 10.0303L11.0303 15.0303C10.7374 15.3232 10.2626 15.3232 9.96967 15.0303L7.96967 13.0303C7.67678 12.7374 7.67678 12.2626 7.96967 11.9697C8.26256 11.6768 8.73744 11.6768 9.03033 11.9697L10.5 13.4393L12.7348 11.2045L14.9697 8.96967C15.2626 8.67678 15.7374 8.67678 16.0303 8.96967Z"
            fill="#14532D"
          />{" "}
        </g>
      </svg>
    ) : status === "warning" ? (
      <IconWarning color="#713F12" className="instance-node" />
    ) : status === "error" ? (
      <IconXSizeMd className="instance-node" color="#7F1D1D" />
    ) : status === "information" ? (
      <Info color="#4236f5" />
    ) : (
      <IconLoading className="instance-node rotating-img" color="#4236f5" />
    );

  return (
    <div className={`alert variant-${variant} ${type} ${justify} ${status} ${className}`} style={style}>
      {type === "one" && (
        <>
          <>
            {icon && (
              <div className="alert-icon">
                {status === "success" && (
                  <IconCheck
                    className="icons"
                    color={
                      ["left-accent", "subtle", "top-accent"].includes(variant)
                        ? "white"
                        : variant === "solid"
                        ? "#16A34A"
                        : undefined
                    }
                  />
                )}

                {status === "warning" && (
                  <IconWarning
                    className="icons"
                    color={
                      ["left-accent", "subtle", "top-accent"].includes(variant)
                        ? "white"
                        : variant === "solid"
                        ? "#EAB308"
                        : undefined
                    }
                  />
                )}

                {status === "info" && (
                  <IconInfo
                    className="icons"
                    color={
                      ["left-accent", "subtle", "top-accent"].includes(variant)
                        ? "white"
                        : variant === "solid"
                        ? "#00A3C4"
                        : undefined
                    }
                  />
                )}

                {status === "loading" && (
                  <IconInfo
                    className="icons"
                    color={
                      ["left-accent", "subtle", "top-accent"].includes(variant)
                        ? "white"
                        : variant === "solid"
                        ? "#00A3C4"
                        : undefined
                    }
                  />
                )}

                {status === "error" && (
                  <IconX
                    className="icons"
                    color={
                      ["left-accent", "subtle", "top-accent"].includes(variant)
                        ? "white"
                        : variant === "solid"
                        ? "#DC2626"
                        : undefined
                    }
                  />
                )}
              </div>
            )}
          </>
          <div className="stack">
            {title && <div className="alert-title">Title</div>}

            <div className="alert-description" dangerouslySetInnerHTML={{ __html: descriptionHtml }}></div>
          </div>
        </>
      )}

      {type === "two" && (
        <div className="div">
          {icon && (
            <div className="icons-wrapper">
              {status === "success" && (
                <IconCheck
                  className="icons"
                  color={variant === "solid" ? "#16A34A" : variant === "subtle" ? "white" : undefined}
                />
              )}

              {status === "error" && (
                <IconX
                  className="icons"
                  color={variant === "solid" ? "#DC2626" : variant === "subtle" ? "white" : undefined}
                />
              )}

              {status === "warning" && (
                <IconWarning
                  className="icons"
                  color={variant === "solid" ? "#EAB308" : variant === "subtle" ? "white" : undefined}
                />
              )}

              {status === "info" && (
                <IconInfo
                  className="icons"
                  color={variant === "solid" ? "#00A3C4" : variant === "subtle" ? "white" : undefined}
                />
              )}

              {status === "loading" && (
                <IconInfo
                  className="icons"
                  color={variant === "solid" ? "#00A3C4" : variant === "subtle" ? "white" : undefined}
                />
              )}
            </div>
          )}

          <div className="message">
            <div className="text-wrapper" dangerouslySetInnerHTML={{ __html: descriptionHtml }}></div>
          </div>
        </div>
      )}

      {["one", "two"].includes(type) && (
        <IconXSizeMd
          className="instance-node"
          color={variant === "solid" && ["error", "info", "success"].includes(status) ? "white" : "#171923"}
        />
      )}

      {type === "three" && (
        <>
          {/* GEN-3646 — um ramo só para os seis status (antes eram seis cópias que divergiam). A cor do
              status fica no ícone e no título (`<b>` do descriptionHtml ou `heading`); o corpo é cinza,
              como no aviso do Kanban 2.0. `--alert-accent` alimenta o título e o CTA pelo style.css. */}
          <div className="left" style={{ "--alert-accent": (TOAST_TONE[status] ?? TOAST_TONE.info).accent } as React.CSSProperties}>
            {(() => {
              const { Icon, iconColor } = TOAST_TONE[status] ?? TOAST_TONE.info; // status fora do mapa não derruba o aviso
              return (
                <Icon
                  aria-hidden="true"
                  className={`toast-icon${status === "loading" ? " rotating-img" : ""}`}
                  color={iconColor}
                />
              );
            })()}
            <div className="div">
              <div>
                {heading !== undefined || body !== undefined ? (
                  <div className="alert-title-2">
                    {heading && <b className="toast-heading">{heading}</b>}
                    {body && <div className="toast-body">{body}</div>}
                  </div>
                ) : (
                  <div className="alert-title-2" dangerouslySetInnerHTML={{ __html: descriptionHtml }}></div>
                )}
                {clicable && clicable?.newLine && (
                  <div className="alert-clicable" onClick={clicable.action}>
                    {clicable.text}
                  </div>
                )}
              </div>
              {clicable && !clicable?.newLine && (
                <div className="alert-clicable" onClick={clicable.action}>
                  {clicable.text}
                </div>
              )}
            </div>
          </div>

          <>{isClosable && <BotaoFecharAlerta onClose={closableFunc} />}</>
        </>
      )}

      {type === "four" && (
        <>
          <div className="left w-100">
            {status === "success" && (
              <svg width="32px" height="32px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_bgCarrier" stroke-width="0" />
                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                <g id="SVGRepo_iconCarrier">
                  {" "}
                  <path
                    opacity="0.3"
                    d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                    fill="#4AD17C"
                  />{" "}
                  <path
                    d="M16.0303 8.96967C16.3232 9.26256 16.3232 9.73744 16.0303 10.0303L11.0303 15.0303C10.7374 15.3232 10.2626 15.3232 9.96967 15.0303L7.96967 13.0303C7.67678 12.7374 7.67678 12.2626 7.96967 11.9697C8.26256 11.6768 8.73744 11.6768 9.03033 11.9697L10.5 13.4393L12.7348 11.2045L14.9697 8.96967C15.2626 8.67678 15.7374 8.67678 16.0303 8.96967Z"
                    fill="#14532D"
                  />{" "}
                </g>
              </svg>
            )}

            {status === "warning" && <IconWarning color="#713F12" className="instance-node" />}

            {["success", "warning"].includes(status) && (
              <Flex justifyContent="space-between" alignItems="center" w="100%">
                <div>
                  <div
                    className="alert-title-2"
                    style={{ color: status === "success" ? "#14532D" : "#713F12" }}
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  ></div>
                </div>
                {clicable && !clicable?.newLine && (
                  <Button
                    variant={"outline"}
                    fontSize={"14px"}
                    onClick={clicable.action}
                    leftIcon={<Download color="#1a202c" height="14px" />}
                    backgroundColor="#ffffff"
                    color="#1a202c"
                  >
                    {clicable.text}
                  </Button>
                )}
              </Flex>
            )}

            {status === "error" && (
              <>
                <IconXSizeMd className="instance-node" color="#7F1D1D" />
                <div className="div">
                  <div
                    className="alert-title-2"
                    style={{ color: "#7F1D1D" }}
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  ></div>
                  {clicable ? (
                    <div className="alert-clicable" onClick={clicable.action}>
                      {clicable.text}
                    </div>
                  ) : null}
                </div>
              </>
            )}

            {status === "info" && (
              <>
                <IconLoading className="instance-node rotating-img" color={"#4b4eff"} />
                <div className="div">
                  <div
                    className="alert-title-2"
                    style={{ color: "#4b4eff" }}
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  ></div>
                  {clicable ? (
                    <div className="alert-clicable" onClick={clicable.action}>
                      {clicable.text}
                    </div>
                  ) : null}
                </div>
              </>
            )}

            {status === "information" && (
              <>
                <Info color={"#4b4eff"} />
                <div className="div">
                  <div
                    className="alert-title-2"
                    style={{ color: "#4b4eff" }}
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  ></div>
                  {clicable ? (
                    <div className="alert-clicable" onClick={clicable.action}>
                      {clicable.text}
                    </div>
                  ) : null}
                </div>
              </>
            )}

            {status === "loading" && (
              <>
                <IconLoading className="instance-node rotating-img" color={"#4b4eff"} />
                <div className="div">
                  <div
                    className="alert-title-2"
                    style={{ color: "#4b4eff" }}
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  ></div>
                  {clicable ? (
                    <div className="alert-clicable" onClick={clicable.action}>
                      {clicable.text}
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>

          <>
            {isClosable && (
              <BotaoFecharAlerta onClose={closableFunc} />
            )}
          </>
        </>
      )}

      {/* Banner multi-assunto (GEN-2407 R8): ícone único à esquerda, uma linha por assunto com
          o CTA da própria linha alinhado à direita, e um único X para a faixa inteira. */}
      {type === "banner" && rows && rows.length > 0 && (
        <>
          <div className="banner-left" style={{ alignItems: "flex-start" }}>
            {icon && bannerIcon}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
              {rows.map((row, index) => (
                <div
                  key={index}
                  style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}
                >
                  <div
                    className="alert-title-2"
                    style={{ color: bannerTextColor }}
                    dangerouslySetInnerHTML={{ __html: row.html }}
                  ></div>
                  {row.action && (
                    <div
                      className="alert-clicable banner-cta"
                      style={{ color: bannerTextColor, flexShrink: 0 }}
                      onClick={row.action.onClick}
                    >
                      {row.action.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {isClosable && (
            <BotaoFecharAlerta onClose={closableFunc} />
          )}
        </>
      )}

      {type === "banner" && !(rows && rows.length > 0) && (
        <>
          <div className="banner-left">
            {icon && bannerIcon}
            <div
              className="alert-title-2"
              style={{ color: bannerTextColor }}
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            ></div>
          </div>

          {/* GEN-777: ação SECUNDÁRIA, à esquerda da principal. Aditiva — banner que não a
              passa continua com o layout de sempre. Nasceu do detalhamento de CDAs: ao terminar,
              o banner precisa oferecer a planilha ("Baixar") ao lado de "Ver resultados", porque
              o arquivo é o motivo pelo qual o usuário disparou o lote. */}
          {secondaryClicable && (
            <div
              className="alert-clicable banner-cta"
              style={{
                color: bannerTextColor,
                flexShrink: 0,
                marginRight: 16,
                opacity: secondaryClicable.disabled ? 0.5 : 1,
                pointerEvents: secondaryClicable.disabled ? "none" : undefined,
                cursor: secondaryClicable.disabled ? "default" : undefined,
              }}
              onClick={secondaryClicable.disabled ? undefined : secondaryClicable.action}
            >
              {secondaryClicable.text}
            </div>
          )}

          {clicable &&
            (clicable.variant === "button" ? (
              <Button
                className="banner-cta-button"
                variant={"outline"}
                fontSize={"14px"}
                fontWeight={600}
                onClick={clicable.action}
                backgroundColor="#ffffff"
                borderColor={bannerTextColor}
                color={bannerTextColor}
                flexShrink={0}
              >
                {clicable.text}
              </Button>
            ) : (
              <div className="alert-clicable banner-cta" style={{ color: bannerTextColor }} onClick={clicable.action}>
                {clicable.text}
              </div>
            ))}

          {isClosable && (
            <BotaoFecharAlerta onClose={closableFunc} />
          )}
        </>
      )}
    </div>
  );
};

CustomAlert.propTypes = {
  isClosable: PropTypes.bool,
  icon: PropTypes.bool,
  title: PropTypes.bool,
  status: PropTypes.oneOf(["warning", "success", "error", "info", "loading", "information"]),
  variant: PropTypes.oneOf(["top-accent", "solid", "subtle", "alert", "left-accent"]),
  type: PropTypes.oneOf(["two", "three", "one", "four", "banner"]),
};
